import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WidgetRef } from '@trinity/chat/types/dashboard-config';
import type { WidgetMapContextValue } from '../../containers/WidgetGrid/context.js';
import type { LibraryWidgetState, WidgetLibraryContextValue } from './interfaces.js';

const WidgetLibraryContext = createContext<WidgetLibraryContextValue | null>(null);

// Outside a provider nothing resolves, so the component map is never read.
const NO_LIBRARIES: WidgetLibraryContextValue = { libraries: {}, widgetComponentMap: {} as WidgetMapContextValue };

export function WidgetLibraryProvider({
  libraries,
  widgetComponentMap,
  children,
}: WidgetLibraryContextValue & { children: React.ReactNode }): React.ReactElement {
  const value = useMemo(() => ({ libraries, widgetComponentMap }), [libraries, widgetComponentMap]);
  return <WidgetLibraryContext.Provider value={value}>{children}</WidgetLibraryContext.Provider>;
}

/** The host's libraries; empty outside a provider, so every pointer resolves as `unavailable`. */
export function useWidgetLibraries(): WidgetLibraryContextValue {
  return useContext(WidgetLibraryContext) ?? NO_LIBRARIES;
}

/**
 * Resolves a pointer: the preloaded list first, then the library's `fetchWidget`, else `unavailable`
 * when the app supplies no library for that source.
 */
export function useLibraryWidget(ref: WidgetRef): LibraryWidgetState {
  const { libraries } = useWidgetLibraries();
  const library = libraries[ref.source];
  const preloaded = useMemo(
    () => library?.widgets?.find((w) => w.id === ref.widgetId),
    [library?.widgets, ref.widgetId],
  );
  const fetchWidget = library?.fetchWidget;
  const cacheKey = library?.cacheKey;
  const cacheable = cacheKey !== undefined;
  const lazy = useQuery({
    queryKey: ['widget-library', ref.source, cacheKey ?? null, ref.widgetId],
    queryFn: () => (fetchWidget ? fetchWidget(ref.widgetId) : Promise.resolve(null)),
    enabled: Boolean(fetchWidget) && !preloaded && !library?.widgets,
    // No cacheKey, no sharing: the entry is dropped as soon as nothing renders it.
    staleTime: cacheable ? 60_000 : 0,
    gcTime: cacheable ? undefined : 0,
  });

  if (!library) return { status: 'unavailable' };
  if (preloaded) return { status: 'ready', widget: preloaded };
  if (library.widgets) return { status: 'missing' };
  if (library.fetchWidget) {
    if (lazy.isError) return { status: 'error' };
    if (lazy.isPending) return { status: 'loading' };
    return lazy.data ? { status: 'ready', widget: lazy.data } : { status: 'missing' };
  }
  return library.isError ? { status: 'error' } : { status: 'loading' };
}
